// Customer Follow-ups admin: switch it on safely, decide who does what, see
// the whole team's queue, and read what actually happened to customers.
// Every section is gated by the server; a section shown here that the server
// refuses simply says so.
import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { API } from '../api';
import { btn, quiet, GREEN, ist, errMsg, WfBadge } from './FollowUps';

const F = `${API}/api/followups`;
const WINE = '#6E1F2A';
const MUTED = '#7d6a63';
const WFL = { failed_delivery: 'Delivery problems', abandoned_checkout: 'Unfinished checkouts', review: 'Review requests' };
const PERM_LABEL = {
  work: 'Work customers', team_view: 'See whole team', assign: 'Assign / transfer', manage: 'Rules & templates',
  reports: 'Reports', export: 'Export data', courier: 'Courier reattempts'
};
const EXCLUDED = {
  order_not_found: 'order not in our system', order_cancelled: 'order cancelled', no_usable_phone: 'no usable phone number',
  opted_out_all: 'asked for no messages', opted_out: 'opted out', already_open: 'already has an open case',
  closed_earlier: 'handled earlier', already_purchased: 'already ordered', waiting_for_delay: 'too recent (waiting for the delay)',
  no_marketing_consent: 'did not accept marketing', merged_into_open_case: 'joins their existing case',
  said_not_interested_recently: 'said not interested recently', asked_in_old_reviews_tab: 'already asked in the old Reviews tab',
  review_already_received: 'review already received', has_return_or_exchange: 'has a return/exchange',
  not_all_parcels_delivered: 'not every parcel delivered yet', asked_in_last_30_days: 'asked in the last 30 days'
};

const Section = ({ title, children, right }) => (
  <div className="card">
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
      <div className="card-title" style={{ marginBottom: 0 }}>{title}</div>{right}
    </div>
    {children}
  </div>
);

// ------------------------------------------------------------- setup ---

function Setup({ me }) {
  const [cfg, setCfg] = useState(null);
  const [preview, setPreview] = useState(null);
  const [status, setStatus] = useState(null);
  const [busy, setBusy] = useState(null);
  const [error, setError] = useState(null);
  const canManage = me.perms.includes('manage');

  const load = useCallback(async () => {
    try {
      const [c, s] = await Promise.all([axios.get(`${F}/admin/config`), axios.get(`${F}/admin/sync-status`)]);
      setCfg(c.data.config); setStatus(s.data);
    } catch (e) { setError(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const runPreview = async (sync) => {
    setBusy('preview'); setError(null);
    try { setPreview((await axios.get(`${F}/admin/preview`, { params: sync ? { sync: 1 } : {}, timeout: 180000 })).data); await load(); }
    catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  };
  const toggle = async (enabled) => {
    if (enabled && !window.confirm('Switch the queue on? Staff will start getting customers from "Start next customer". Nothing is sent automatically.')) return;
    if (!enabled && !window.confirm('Switch Customer Follow-ups off? Nobody can take or message customers until it is on again. Nothing is deleted.')) return;
    setBusy('toggle');
    try { await axios.put(`${F}/admin/config`, { config: { enabled }, previewSeen: !!preview }); await load(); }
    catch (e) { setError(errMsg(e)); }
    finally { setBusy(null); }
  };

  if (!cfg) return error ? <div className="card" style={{ color: '#b3261e' }}>{error}</div> : <div style={{ color: MUTED }}>Loading…</div>;
  return (
    <>
      <Section title={cfg.enabled ? 'Queue is ON' : 'Queue is OFF'} right={canManage && (
        cfg.enabled
          ? <button style={{ ...quiet, color: '#b3261e' }} disabled={!!busy} onClick={() => toggle(false)}>Switch off</button>
          : <button style={{ ...btn, background: preview ? GREEN : '#bdb2ab', color: '#fff' }} disabled={!!busy || !preview} onClick={() => toggle(true)} title={preview ? '' : 'Run the preview first'}>Switch the queue on</button>
      )}>
        <div style={{ fontSize: 14, color: MUTED }}>
          {cfg.enabled ? `On since ${ist(cfg.activatedAt)}.` : 'Nothing is synced, created or claimable while off. Run the preview, read it, then switch on.'}
          {' '}Staff only ever message customers themselves through WhatsApp — this app never sends anything automatically.
        </div>
        {canManage && (
          <div style={{ display: 'flex', gap: 8, marginTop: 12, flexWrap: 'wrap' }}>
            <button style={{ ...btn, background: WINE, color: '#fff' }} disabled={!!busy} onClick={() => runPreview(true)}>{busy === 'preview' ? 'Fetching from Delhivery and Shopify…' : 'Run preview (fetch latest)'}</button>
            <button style={quiet} disabled={!!busy} onClick={() => runPreview(false)}>Preview from saved data</button>
          </div>
        )}
        {error && <div style={{ color: '#b3261e', marginTop: 8 }}>{error}</div>}
      </Section>

      {preview && (
        <Section title="Preview — what switching on would create">
          {preview.dataNotes.map((n, i) => <div key={i} style={{ fontSize: 13, color: '#8a5a2b', background: '#fbeee5', borderRadius: 8, padding: '6px 10px', marginBottom: 6 }}>{n}</div>)}
          {['failed_delivery', 'abandoned_checkout', 'review'].map(w => {
            const p = preview[w];
            if (!p) return null;
            return (
              <div key={w} style={{ marginTop: 14 }}>
                <div style={{ fontWeight: 700 }}>{WFL[w]}: {p.wouldCreate} new</div>
                {Object.keys(p.excluded).length > 0 && (
                  <div style={{ fontSize: 13, color: MUTED }}>Left out: {Object.entries(p.excluded).map(([k, n]) => `${n} ${EXCLUDED[k] || k}`).join(' · ')}</div>
                )}
                {p.examples.length > 0 && (
                  <div style={{ overflowX: 'auto', marginTop: 6 }}>
                    <table><thead><tr><th>Customer</th><th>Order</th><th>Why</th><th>Since</th></tr></thead>
                      <tbody>{p.examples.slice(0, 40).map((x, i) => (
                        <tr key={i}><td>{x.name || '—'}{x.urgent && <span className="badge badge-red" style={{ marginLeft: 6 }}>urgent</span>}</td><td>{x.order || '—'}</td><td>{x.reason}</td><td>{ist(x.since)}</td></tr>
                      ))}</tbody></table>
                  </div>
                )}
              </div>
            );
          })}
        </Section>
      )}

      {status && (
        <Section title="Data sources" right={canManage && <button style={quiet} disabled={!!busy} onClick={async () => { setBusy('sync'); try { await axios.post(`${F}/admin/sync`, null, { timeout: 180000 }); await load(); } catch (e) { setError(errMsg(e)); } finally { setBusy(null); } }}>{busy === 'sync' ? 'Syncing…' : 'Sync now'}</button>}>
          <table><thead><tr><th>Source</th><th>Last success</th><th>Problem</th></tr></thead><tbody>
            {[['shipments', 'Delhivery parcels'], ['checkouts', 'Shopify abandoned checkouts'], ['purchases', 'Order matching'], ['detection', 'Case creation'], ['ownership', 'Hold expiry']].map(([k, label]) => {
              const s = status.status[k] || {};
              const stale = s.lastOkAt && Date.now() - new Date(s.lastOkAt) > 3600000;
              return <tr key={k}><td>{label}</td><td style={{ color: stale ? '#8c1d18' : undefined }}>{s.lastOkAt ? ist(s.lastOkAt) : 'never'}{stale ? ' (stale)' : ''}</td><td style={{ color: '#b3261e', fontSize: 12 }}>{s.lastError || ''}</td></tr>;
            })}
          </tbody></table>
          <div style={{ fontSize: 12, color: MUTED, marginTop: 8 }}>
            {status.local.shipmentsTracked} parcels tracked ({status.local.shipmentsWithFailures} with a failed attempt) · {status.local.checkoutsHeld} checkouts held · {status.local.openCases} open cases
          </div>
        </Section>
      )}
    </>
  );
}

// -------------------------------------------------------------- team ---

function Team({ me }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => {
    try { setData((await axios.get(`${F}/admin/team`)).data); } catch (e) { setError(errMsg(e)); }
  }, []);
  useEffect(() => { load(); }, [load]);
  const patch = async (u, body) => { try { await axios.patch(`${F}/admin/team/${encodeURIComponent(u.email)}`, body); load(); } catch (e) { alert(errMsg(e)); } };
  const releaseAll = async (u) => {
    const reason = window.prompt(`Hand ALL of ${u.email}'s customers back to the queue? Their history stays with each customer.\n\nReason:`);
    if (!reason) return;
    try { const { data: r } = await axios.post(`${F}/admin/release-all`, { email: u.email, reason }); alert(`${r.released} customer(s) released.`); load(); }
    catch (e) { alert(errMsg(e)); }
  };
  if (error) return <div className="card" style={{ color: '#b3261e' }}>{error}</div>;
  if (!data) return <div style={{ color: MUTED }}>Loading…</div>;
  const chip = (on, can) => ({ fontSize: 12, padding: '3px 9px', borderRadius: 20, cursor: can ? 'pointer' : 'default', border: '1px solid ' + (on ? WINE : '#e6dccc'), background: on ? '#57161f' : '#f8f3eb', color: on ? '#fff' : '#93827a', opacity: can ? 1 : 0.6, display: 'inline-block', margin: 2 });
  const canAssign = me.perms.includes('assign') || me.perms.includes('manage');
  return (
    <Section title="Who does follow-ups">
      <div style={{ fontSize: 13, color: MUTED, marginBottom: 10 }}>
        {data.canGrant ? 'Tick the tab and permissions per person. ' : 'Only the owner can grant permissions. '}
        "Work customers" is what Grace and Rubul need. Unavailable = the queue gives them nothing new; their own customers stay theirs.
      </div>
      {data.users.filter(u => u.role !== 'owner').map(u => (
        <div key={u.email} style={{ borderTop: '1px solid #f0e9dd', padding: '10px 0', opacity: u.active ? 1 : 0.5 }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', flexWrap: 'wrap', gap: 6 }}>
            <div><b>{u.name || u.email}</b> <span style={{ fontSize: 12, color: MUTED }}>{u.email}{!u.active ? ' · suspended' : ''} · holding {u.holding}</span></div>
            <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} disabled={!data.canGrant} checked={u.hasTab} onChange={() => patch(u, { hasTab: !u.hasTab })} /> Follow-ups tab</label>
          </div>
          <div style={{ marginTop: 4 }}>
            {data.allPerms.map(p => {
              const on = u.grantedPerms.includes(p);
              return <span key={p} style={chip(on, data.canGrant)} onClick={() => data.canGrant && patch(u, { perms: on ? u.grantedPerms.filter(x => x !== p) : [...u.grantedPerms, p] })}>{PERM_LABEL[p]}</span>;
            })}
          </div>
          <div style={{ marginTop: 4, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
            <span style={{ color: MUTED }}>Works on:</span>
            {data.allWorkflows.map(w => {
              const on = !u.workflows.length || u.workflows.includes(w);
              const next = on ? (u.workflows.length ? u.workflows : data.allWorkflows).filter(x => x !== w) : [...u.workflows, w];
              return <span key={w} style={chip(on, canAssign)} onClick={() => canAssign && next.length && patch(u, { workflows: next })}>{WFL[w]}</span>;
            })}
          </div>
          <div style={{ marginTop: 6, display: 'flex', gap: 10, flexWrap: 'wrap', alignItems: 'center', fontSize: 13 }}>
            <label><input type="checkbox" style={{ width: 'auto' }} disabled={!canAssign} checked={u.available} onChange={() => patch(u, { available: !u.available })} /> Available today</label>
            <label>Max customers at once <input type="number" min={1} style={{ width: 70 }} disabled={!canAssign} defaultValue={u.maxActive || ''} placeholder={String(data.defaultLimit)}
              onBlur={e => patch(u, { maxActive: e.target.value ? Number(e.target.value) : null })} /></label>
            {u.holding > 0 && me.perms.includes('assign') && <button style={{ ...quiet, fontSize: 12, padding: '4px 10px' }} onClick={() => releaseAll(u)}>Release all to queue</button>}
          </div>
        </div>
      ))}
    </Section>
  );
}

// ------------------------------------------------------------- queue ---

function Queue({ onOpen }) {
  const [data, setData] = useState(null);
  const [owner, setOwner] = useState('');
  const [wf, setWf] = useState('');
  const [error, setError] = useState(null);
  useEffect(() => {
    (async () => {
      try { setData((await axios.get(`${F}/admin/queue`, { params: { owner: owner || undefined, workflow: wf || undefined } })).data); }
      catch (e) { setError(errMsg(e)); }
    })();
  }, [owner, wf]);
  if (error) return <div className="card" style={{ color: '#b3261e' }}>{error}</div>;
  return (
    <Section title={`Team queue${data ? ` (${data.total})` : ''}`} right={
      <span style={{ display: 'flex', gap: 6 }}>
        <select value={wf} onChange={e => setWf(e.target.value)} style={{ width: 'auto' }}><option value="">All kinds</option>{Object.entries(WFL).map(([k, v]) => <option key={k} value={k}>{v}</option>)}</select>
        <select value={owner} onChange={e => setOwner(e.target.value)} style={{ width: 'auto' }}><option value="">Everyone</option><option value="none">Unassigned</option></select>
      </span>}>
      {!data ? <div style={{ color: MUTED }}>Loading…</div> : (
        <div style={{ overflowX: 'auto' }}>
          <table><thead><tr><th>Customer</th><th>Kinds</th><th>With</th><th>Next</th><th>Due</th></tr></thead>
            <tbody>{data.rows.map(r => (
              <tr key={r.contactId} onClick={() => onOpen(r.contactId)} style={{ cursor: 'pointer' }}>
                <td>{r.name}{r.urgent && <span className="badge badge-red" style={{ marginLeft: 6 }}>urgent</span>}</td>
                <td>{r.workflows.map(w => <WfBadge key={w} wf={w} />)}</td>
                <td>{r.owner ? `${r.owner.split('@')[0]}${r.ownerState === 'reserved' ? ' (reserved)' : ''}` : <span style={{ color: MUTED }}>queue</span>}</td>
                <td style={{ fontSize: 13 }}>{r.escalatedTo ? `Escalated → ${r.escalatedTo}` : r.nextAction}</td>
                <td style={{ color: r.overdue ? '#8c1d18' : undefined, fontSize: 13 }}>{ist(r.nextDue)}</td>
              </tr>
            ))}</tbody></table>
        </div>
      )}
    </Section>
  );
}

// -------------------------------------------------------- reattempts ---

function Reattempts({ onOpen }) {
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const load = useCallback(async () => { try { setData((await axios.get(`${F}/tasks/reattempts`)).data); } catch (e) { setError(errMsg(e)); } }, []);
  useEffect(() => { load(); }, [load]);
  const submit = async (t, via) => {
    if (via === 'manual_panel' && !window.confirm(`Have you submitted the reattempt for AWB ${t.awb} in the Delhivery panel? Only say yes if you have.`)) return;
    try { await axios.post(`${F}/cases/${t.caseId}/reattempt`, { via, confirm: true }); load(); }
    catch (e) { alert(errMsg(e)); }
  };
  if (error) return <div className="card" style={{ color: '#b3261e' }}>{error}</div>;
  if (!data) return <div style={{ color: MUTED }}>Loading…</div>;
  return (
    <Section title={`Reattempts to submit (${data.tasks.length})`}>
      <div style={{ fontSize: 13, color: MUTED, marginBottom: 8 }}>
        Customers confirmed these should be delivered again. {data.apiEnabled ? 'You can send them straight to Delhivery, or submit in the Delhivery panel and record it here.' : 'Submit each in the Delhivery panel (NDR / Pending actions), then record it here. Sending straight from this app is switched off until it has been tested on a real parcel.'}
        {' '}The screen shows "courier acknowledged" only when Delhivery's own scan says so.
      </div>
      {data.tasks.length === 0 ? <div style={{ color: MUTED }}>Nothing waiting.</div> : data.tasks.map(t => (
        <div key={t.caseId} style={{ borderTop: '1px solid #f0e9dd', padding: '8px 0', display: 'flex', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
          <div style={{ cursor: 'pointer' }} onClick={() => onOpen(t.contactId)}>
            <b>AWB {t.awb}</b> · {t.order} · {t.name}
            <div style={{ fontSize: 12, color: MUTED }}>{t.reason} · confirmed {ist(t.openedAt)}{t.unavailableUntil ? ` · deliver after ${ist(t.unavailableUntil)}` : ''} · {t.owner?.split('@')[0]}</div>
          </div>
          <span style={{ display: 'flex', gap: 6 }}>
            {data.apiEnabled && <button style={{ ...btn, background: WINE, color: '#fff', fontSize: 13 }} onClick={() => submit(t, 'api')}>Send to Delhivery</button>}
            <button style={{ ...quiet, fontSize: 13 }} onClick={() => submit(t, 'manual_panel')}>I submitted it in Delhivery</button>
          </span>
        </div>
      ))}
    </Section>
  );
}

// ----------------------------------------------------------- reports ---

function Reports({ me }) {
  const [days, setDays] = useState(7);
  const [team, setTeam] = useState(null);
  const [mine, setMine] = useState(null);
  const [error, setError] = useState(null);
  useEffect(() => {
    (async () => {
      try {
        if (me.perms.includes('work')) setMine((await axios.get(`${F}/reports/me`, { params: { days } })).data);
        if (me.perms.includes('reports')) setTeam((await axios.get(`${F}/reports/team`, { params: { days } })).data);
      } catch (e) { setError(errMsg(e)); }
    })();
  }, [days, me.perms]);
  const Stat = ({ label, n }) => <div className="stat-card"><div className="stat-label">{label}</div><div className="stat-value">{n ?? '—'}</div></div>;
  return (
    <>
      <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap' }}>
        {[1, 7, 30].map(d => <button key={d} onClick={() => setDays(d)} style={{ ...quiet, padding: '6px 12px', fontSize: 13, background: days === d ? WINE : '#fff', color: days === d ? '#fff' : '#4a3840' }}>{d === 1 ? 'Today' : `Last ${d} days`}</button>)}
        {me.perms.includes('export') && <a href={`${F}/export.csv?days=${days}`} style={{ ...quiet, padding: '6px 12px', fontSize: 13 }}>Download CSV</a>}
      </div>
      {error && <div className="card" style={{ color: '#b3261e' }}>{error}</div>}
      {mine && (
        <Section title="Me">
          <div className="stat-grid">
            <Stat label="Customers contacted" n={mine.customersContacted} /><Stat label="Follow-ups due" n={mine.followupsDue} />
            <Stat label="Overdue" n={mine.overdue} /><Stat label="Awaiting replies" n={mine.awaitingReply} /><Stat label="Completed" n={mine.completed} />
          </div>
        </Section>
      )}
      {team && (
        <>
          <Section title="Queue and ageing">
            <table><thead><tr><th>Kind</th><th>In queue</th><th>With staff</th><th>Escalated</th><th>&lt;1 day</th><th>1–3 days</th><th>3+ days</th></tr></thead>
              <tbody>{Object.entries(team.queue).map(([w, q]) => <tr key={w}><td>{WFL[w]}</td><td>{q.inQueue}</td><td>{q.withStaff}</td><td>{q.escalated}</td><td>{q.age.under1d}</td><td>{q.age.d1to3}</td><td style={{ color: q.age.over3d ? '#8c1d18' : undefined }}>{q.age.over3d}</td></tr>)}</tbody></table>
          </Section>
          <Section title="Workload">
            <div style={{ overflowX: 'auto' }}><table><thead><tr><th>Person</th><th>Holding</th><th>Due</th><th>Overdue</th><th>Contacted</th><th>Completed</th></tr></thead>
              <tbody>{team.workload.map(w => <tr key={w.email}><td>{w.name || w.email}{!w.available && <span style={{ color: MUTED }}> (unavailable)</span>}</td><td>{w.holding}/{w.limit}</td><td>{w.due}</td><td style={{ color: w.overdue ? '#8c1d18' : undefined }}>{w.overdue}</td><td>{w.customersContacted}</td><td>{w.completed}</td></tr>)}</tbody></table></div>
            <div style={{ fontSize: 12, color: MUTED, marginTop: 6 }}>Counted per customer, not per message — sending more messages does not raise any number here.</div>
          </Section>
          <Section title="Failed deliveries (finished in this period)">
            <div className="stat-grid">
              <Stat label="Delivered after we contacted" n={team.delivery.deliveredAfterContact} /><Stat label="Delivered, no contact" n={team.delivery.deliveredWithoutContact} />
              <Stat label="Returned after contact" n={team.delivery.returnedAfterContact} /><Stat label="Returned, no contact" n={team.delivery.returnedWithoutContact} />
              <Stat label="Reattempts submitted" n={team.delivery.reattemptsSubmitted} /><Stat label="Courier acknowledged" n={team.delivery.courierAcknowledged} />
            </div>
          </Section>
          <Section title="Unfinished checkouts">
            <div className="stat-grid">
              <Stat label="Customers contacted" n={team.abandoned.customersContacted} /><Stat label="Ordered after our message" n={team.abandoned.purchasedAfterOutreach} />
              <Stat label="Proven (approved offer used)" n={team.abandoned.provenRecovered} /><Stat label="Ordered before any message" n={team.abandoned.purchasedBeforeContact} />
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>{team.abandoned.explanation}</div>
          </Section>
          <Section title="Reviews">
            <div className="stat-grid">
              <Stat label="Verified reviews received" n={team.reviews.verifiedReceived} /><Stat label="…after our message" n={team.reviews.verifiedAfterContact} />
              <Stat label="Confirmed by staff (unverified)" n={team.reviews.manuallyConfirmed} /><Stat label="Links shared" n={team.reviews.linksShared} />
              <Stat label="Declined" n={team.reviews.declined} /><Stat label="Problems raised" n={team.reviews.problemsRaised} />
            </div>
            <div style={{ fontSize: 12, color: MUTED }}>{team.reviews.note}</div>
          </Section>
          <Section title="Replies, escalations, opt-outs">
            <table><thead><tr><th>Kind</th><th>Messaged</th><th>Replied</th><th>Reply rate</th></tr></thead>
              <tbody>{Object.entries(team.responses).map(([w, r]) => <tr key={w}><td>{WFL[w]}</td><td>{r.messaged}</td><td>{r.replied}</td><td>{r.rate == null ? '—' : r.rate + '%'}</td></tr>)}</tbody></table>
            <div style={{ fontSize: 12, color: MUTED, margin: '6px 0 12px' }}>Replies are what staff recorded — the app cannot read WhatsApp.</div>
            <div style={{ fontSize: 14 }}>Escalations: {team.escalations.count} ({team.escalations.open} still open) · Opt-outs: {team.optOuts.total} ({team.optOuts.all} no messages at all) · Sends blocked by the rules: {team.limits.sendsBlocked} · Cases over the attempt limit: {team.limits.casesOverAttemptLimit}</div>
            {team.escalations.reasons.length > 0 && <ul style={{ fontSize: 13, marginTop: 6, paddingLeft: 18 }}>{team.escalations.reasons.slice(-10).map((e, i) => <li key={i}>{WFL[e.workflow] || ''}: {e.reason}</li>)}</ul>}
          </Section>
        </>
      )}
    </>
  );
}

// ---------------------------------------------------------- settings ---

function Settings({ me }) {
  const [cfg, setCfg] = useState(null);
  const [saved, setSaved] = useState(null);
  const [error, setError] = useState(null);
  const canEdit = me.perms.includes('manage');
  useEffect(() => { (async () => { try { setCfg((await axios.get(`${F}/admin/config`)).data.config); } catch (e) { setError(errMsg(e)); } })(); }, []);
  if (error) return <div className="card" style={{ color: '#b3261e' }}>{error}</div>;
  if (!cfg) return <div style={{ color: MUTED }}>Loading…</div>;

  const set = (path, v) => setCfg(c => {
    const n = JSON.parse(JSON.stringify(c));
    const ks = path.split('.'); let o = n;
    for (const k of ks.slice(0, -1)) o = o[k];
    o[ks[ks.length - 1]] = v;
    return n;
  });
  const get = (path) => path.split('.').reduce((o, k) => o?.[k], cfg);
  const num = (path, label, hint) => (
    <label key={path} style={{ fontSize: 13, display: 'block', marginBottom: 8 }}>{label}
      <input type="number" disabled={!canEdit} value={get(path)} onChange={e => set(path, Number(e.target.value))} style={{ display: 'block', width: 120, marginTop: 3 }} />
      {hint && <span style={{ fontSize: 11, color: MUTED }}>{hint}</span>}
    </label>
  );
  const save = async () => {
    try {
      const { enabled, activatedAt, ...rest } = cfg;
      const { data } = await axios.put(`${F}/admin/config`, { config: rest });
      setCfg(data.config); setSaved(data.changed.length ? `Saved: ${data.changed.join(', ')}` : 'No changes');
    } catch (e) { alert(errMsg(e)); }
  };

  return (
    <>
      <Section title="Pause a kind of follow-up">
        {Object.keys(WFL).map(w => (
          <label key={w} style={{ display: 'block', fontSize: 14, marginBottom: 6 }}>
            <input type="checkbox" style={{ width: 'auto' }} disabled={!canEdit} checked={cfg.workflows[w].paused} onChange={e => set(`workflows.${w}.paused`, e.target.checked)} /> Pause {WFL[w].toLowerCase()}
          </label>
        ))}
      </Section>
      <Section title="Contact rules">
        <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap' }}>
          <label style={{ fontSize: 13 }}>Contact hours from (India time)<input type="time" disabled={!canEdit} value={cfg.contactHours.start} onChange={e => set('contactHours.start', e.target.value)} style={{ display: 'block', marginTop: 3 }} /></label>
          <label style={{ fontSize: 13 }}>to<input type="time" disabled={!canEdit} value={cfg.contactHours.end} onChange={e => set('contactHours.end', e.target.value)} style={{ display: 'block', marginTop: 3 }} /></label>
        </div>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6, marginTop: 10 }}>
          {num('cooldownHours', "Gap between messages to one customer (hours)", "Shared across all kinds. Delivery problems can skip it (below).")}
          {num('reservationMinutes', "Untouched claim returns to queue after (minutes)")}
          {num('maxActivePerEmployee', "Max customers per employee")}
          {num('maxAttempts.failed_delivery', "Max messages — delivery problem")}
          {num('maxAttempts.abandoned_checkout', "Max messages — checkout")}
          {num('maxAttempts.review', "Max messages — review")}
          {num('overdueGraceMinutes', "Follow-up counts as overdue after (minutes)")}
          {num('inactiveHours', "Flag a conversation with no action for (hours)")}
          {num('preferPreviousOwnerHours', "Returning customer held for previous owner (hours)")}
        </div>
        <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} disabled={!canEdit} checked={cfg.cooldownExemptDelivery} onChange={e => set('cooldownExemptDelivery', e.target.checked)} /> Delivery problems may skip the gap</label>
      </Section>
      <Section title="When customers become due">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: 6 }}>
          {num('delivery.lookbackHours', "Failed attempts from the last (hours)")}
          {num('delivery.actByHours', "Deadline shown after a failed attempt (hours)")}
          {num('abandoned.delayHours', "Wait after an abandoned checkout (hours)", "Let automated reminders go first.")}
          {num('abandoned.maxAgeHours', "Ignore checkouts older than (hours)")}
          {num('abandoned.attributionDays', "Count an order as 'after our message' within (days)")}
          {num('review.delayDays', "Ask for a review after delivery (days)")}
          {num('review.maxAgeDays', "Don't start reviews for deliveries older than (days)")}
          {num('review.fromOrderNumber', "Reviews only from order number")}
        </div>
        <label style={{ fontSize: 13, display: 'block', marginTop: 6 }}>Abandoned checkouts: contact
          <select disabled={!canEdit} value={cfg.abandoned.consent} onChange={e => set('abandoned.consent', e.target.value)} style={{ display: 'block', marginTop: 3, width: 'auto' }}>
            <option value="accepts_marketing">Only customers who accepted marketing at checkout</option>
            <option value="any">Anyone who left a phone number</option>
          </select>
        </label>
        <label style={{ fontSize: 13, display: 'block', marginTop: 8 }}>Review link (your Google review link; or a product link using {'{handle}'})
          <input disabled={!canEdit} value={cfg.review.linkTemplate} onChange={e => set('review.linkTemplate', e.target.value)} style={{ display: 'block', width: '100%', marginTop: 3 }} />
        </label>
      </Section>
      <Section title="Queue order">
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 6 }}>
          {num('priority.urgent_delivery', "Urgent delivery problem")}
          {num('priority.overdue_followup', "Overdue follow-up")}
          {num('priority.failed_delivery', "Delivery problem")}
          {num('priority.abandoned_checkout', "Unfinished checkout")}
          {num('priority.review', "Review request")}
          {num('priority.agePointsPerHour', "Extra points per hour waiting", "Stops low priorities waiting forever.")}
        </div>
      </Section>
      <Section title="Message templates">
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>
          Fill-ins: {'{name} {staff} {order} {items} {reason} {cod_amount} {checkout_link} {review_link}'}. The first paragraph (with {'{name}'}) is the greeting and is used once when a message covers two reasons.
          Ask for an <b>honest</b> review — never for a star rating, and no rewards unless Dipansh approves them.
        </div>
        {Object.keys(WFL).map(w => (
          <label key={w} style={{ fontSize: 13, display: 'block', marginBottom: 10 }}>{WFL[w]}
            <textarea rows={7} disabled={!canEdit} value={cfg.templates[w]} onChange={e => set(`templates.${w}`, e.target.value)}
              style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 8, border: '1px solid #e6dccc', fontFamily: 'inherit', marginTop: 3 }} />
          </label>
        ))}
      </Section>
      <Section title="Approved offers">
        <div style={{ fontSize: 12, color: MUTED, marginBottom: 8 }}>Staff can give only these. Empty = no offers. The code must already exist on Shopify/GoKwik.</div>
        {cfg.offers.map((o, i) => (
          <div key={i} style={{ display: 'flex', gap: 6, marginBottom: 6, flexWrap: 'wrap' }}>
            <input disabled={!canEdit} value={o.code} placeholder="CODE" onChange={e => set(`offers.${i}.code`, e.target.value.toUpperCase())} style={{ width: 120 }} />
            <input disabled={!canEdit} value={o.description} placeholder="What it gives" onChange={e => set(`offers.${i}.description`, e.target.value)} style={{ flex: 1, minWidth: 160 }} />
            <label style={{ fontSize: 13 }}><input type="checkbox" style={{ width: 'auto' }} disabled={!canEdit} checked={o.active} onChange={e => set(`offers.${i}.active`, e.target.checked)} /> active</label>
            {canEdit && <button style={{ ...quiet, fontSize: 12 }} onClick={() => setCfg(c => ({ ...c, offers: c.offers.filter((_, j) => j !== i) }))}>Remove</button>}
          </div>
        ))}
        {canEdit && <button style={{ ...quiet, fontSize: 13 }} onClick={() => setCfg(c => ({ ...c, offers: [...c.offers, { code: '', description: '', workflows: ['abandoned_checkout'], active: true }] }))}>+ Add offer</button>}
      </Section>
      <Section title="Automated messages & courier">
        <label style={{ fontSize: 13, display: 'block' }}>What staff should know about automated WhatsApps
          <textarea rows={3} disabled={!canEdit} value={cfg.automationNote} onChange={e => set('automationNote', e.target.value)}
            style={{ display: 'block', width: '100%', boxSizing: 'border-box', padding: 8, borderRadius: 8, border: '1px solid #e6dccc', fontFamily: 'inherit', marginTop: 3 }} />
        </label>
        <label style={{ fontSize: 13, display: 'block', marginTop: 10 }}>
          <input type="checkbox" style={{ width: 'auto' }} disabled={!canEdit} checked={cfg.delivery.reattemptApiEnabled}
            onChange={e => { if (!e.target.checked || window.confirm('This lets people with the courier permission send reattempt requests straight to Delhivery. It has not been tested on a real parcel yet — test it once with someone watching the Delhivery panel. Switch on?')) set('delivery.reattemptApiEnabled', e.target.checked); }} />
          {' '}Send reattempt requests straight to Delhivery (untested — off until tried on one real parcel)
        </label>
      </Section>
      {canEdit && <div style={{ position: 'sticky', bottom: 10, textAlign: 'right' }}>
        {saved && <span style={{ fontSize: 13, color: '#2e7d50', marginRight: 10 }}>{saved}</span>}
        <button style={{ ...btn, background: WINE, color: '#fff', padding: '12px 22px' }} onClick={save}>Save settings</button>
      </div>}
    </>
  );
}

// ------------------------------------------------------------- shell ---

export default function FollowUpsAdmin({ me, onOpen }) {
  const P = (p) => me.perms.includes(p);
  const tabs = [
    ['setup', 'Setup', P('manage') || P('assign') || P('team_view')],
    ['team', 'Team', P('manage') || P('assign') || P('team_view')],
    ['queue', 'Team queue', P('team_view')],
    ['reattempts', 'Reattempts', P('courier')],
    ['reports', 'Reports', P('reports') || P('work')],
    ['settings', 'Settings', P('manage') || P('assign') || P('team_view')]
  ].filter(t => t[2]);
  const [tab, setTab] = useState(tabs[0]?.[0]);
  return (
    <div>
      <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap', marginBottom: 12 }}>
        {tabs.map(([k, label]) => (
          <button key={k} onClick={() => setTab(k)} style={{ ...quiet, padding: '6px 12px', fontSize: 13, background: tab === k ? '#f3e7e9' : '#fff', borderColor: tab === k ? WINE : '#e6dccc' }}>{label}</button>
        ))}
      </div>
      {tab === 'setup' && <Setup me={me} />}
      {tab === 'team' && <Team me={me} />}
      {tab === 'queue' && <Queue onOpen={onOpen} />}
      {tab === 'reattempts' && <Reattempts onOpen={onOpen} />}
      {tab === 'reports' && <Reports me={me} />}
      {tab === 'settings' && <Settings me={me} />}
    </div>
  );
}
